/**
 * notifications.ts - Notification services configuration module
 * 
 * CHANGE HISTORY:
 * - 251030_0958 [pending] danielbeer: Created notification services config
 * 
 * Centralizes configuration for Notifyr, Mattermost, and email notifications.
 * Supports tenant-level and user-level overrides.
 */

export const notificationsModule = {
  id: 'services-notifications',
  name: 'Notifications',
  icon: '🔔',
  category: 'services',
  order: 1,
  description: 'Configure notification channels and delivery settings',
  
  settings: {
    // === GENERAL NOTIFICATION SETTINGS ===
    
    enableNotifications: {
      type: 'boolean',
      label: 'Enable Notifications',
      description: 'Master switch for all notifications',
      defaultValue: true,
      scope: ['tenant', 'user']
    },
    
    defaultChannels: {
      type: 'multiselect',
      label: 'Default Channels',
      description: 'Default notification delivery channels',
      defaultValue: ['in-app'],
      options: [
        { label: 'In-App', value: 'in-app' },
        { label: 'Email', value: 'email' },
        { label: 'SMS', value: 'sms' },
        { label: 'Mattermost', value: 'mattermost' },
        { label: 'Push', value: 'push' },
        { label: 'Webhook', value: 'webhook' }
      ],
      scope: ['tenant', 'user']
    },
    
    maxNotifications: {
      type: 'number',
      label: 'Max Stored Notifications',
      description: 'Maximum number of notifications to keep in history',
      defaultValue: 100,
      min: 10,
      max: 1000,
      scope: ['tenant', 'user']
    },
    
    notificationExpiry: {
      type: 'number',
      label: 'Notification Expiry (days)',
      description: 'Auto-delete notifications older than this',
      defaultValue: 30,
      min: 1,
      max: 365,
      scope: ['tenant']
    },
    
    // === MATTERMOST CONFIGURATION ===
    
    mattermostEnabled: {
      type: 'boolean',
      label: 'Enable Mattermost',
      description: 'Enable Mattermost notifications',
      defaultValue: false,
      scope: ['tenant']
    },
    
    mattermostUrl: {
      type: 'string',
      label: 'Mattermost Server URL',
      description: 'Your Mattermost server URL (e.g., https://mattermost.company.com)',
      defaultValue: '',
      placeholder: 'https://mattermost.company.com',
      visible: (settings) => settings['services-notifications.mattermostEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^https?://.+',
        message: 'Must be a valid URL'
      }
    },
    
    mattermostToken: {
      type: 'password',
      label: 'Mattermost Bot Token',
      description: 'Bot access token for Mattermost API',
      defaultValue: '',
      placeholder: 'Enter bot token',
      visible: (settings) => settings['services-notifications.mattermostEnabled'],
      scope: ['tenant'],
      sensitive: true
    },
    
    mattermostDefaultChannel: {
      type: 'string',
      label: 'Default Mattermost Channel',
      description: 'Default channel ID for notifications',
      defaultValue: '',
      placeholder: 'channel-id',
      visible: (settings) => settings['services-notifications.mattermostEnabled'],
      scope: ['tenant']
    },
    
    // === EMAIL CONFIGURATION (via Mailr) ===
    
    emailEnabled: {
      type: 'boolean',
      label: 'Enable Email Notifications',
      description: 'Enable email delivery via Mailr',
      defaultValue: true,
      scope: ['tenant']
    },
    
    mailrApiUrl: {
      type: 'string',
      label: 'Mailr API URL',
      description: 'Mailr container API endpoint',
      defaultValue: 'http://mailr-api:3000',
      placeholder: 'http://mailr-api:3000',
      visible: (settings) => settings['services-notifications.emailEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^https?://.+',
        message: 'Must be a valid URL'
      }
    },
    
    mailrApiKey: {
      type: 'password',
      label: 'Mailr API Key',
      description: 'Optional API key for Mailr authentication',
      defaultValue: '',
      placeholder: 'Enter API key (optional)',
      visible: (settings) => settings['services-notifications.emailEnabled'],
      scope: ['tenant'],
      sensitive: true
    },
    
    mailrFromAddress: {
      type: 'string',
      label: 'From Email Address',
      description: 'Default sender address for notifications',
      defaultValue: 'notifications@company.com',
      placeholder: 'notifications@company.com',
      visible: (settings) => settings['services-notifications.emailEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^[^@]+@[^@]+\\.[^@]+$',
        message: 'Must be a valid email address'
      }
    },
    
    mailrFromName: {
      type: 'string',
      label: 'From Name',
      description: 'Display name for notification emails',
      defaultValue: 'OpCentral Notifications',
      placeholder: 'OpCentral Notifications',
      visible: (settings) => settings['services-notifications.emailEnabled'],
      scope: ['tenant']
    },
    
    // === SMS CONFIGURATION ===
    
    smsEnabled: {
      type: 'boolean',
      label: 'Enable SMS Notifications',
      description: 'Enable SMS delivery (requires Twilio)',
      defaultValue: false,
      scope: ['tenant']
    },
    
    twilioAccountSid: {
      type: 'string',
      label: 'Twilio Account SID',
      description: 'Your Twilio account SID',
      defaultValue: '',
      placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      visible: (settings) => settings['services-notifications.smsEnabled'],
      scope: ['tenant'],
      sensitive: true
    },
    
    twilioAuthToken: {
      type: 'password',
      label: 'Twilio Auth Token',
      description: 'Your Twilio authentication token',
      defaultValue: '',
      placeholder: 'Enter auth token',
      visible: (settings) => settings['services-notifications.smsEnabled'],
      scope: ['tenant'],
      sensitive: true
    },
    
    twilioPhoneNumber: {
      type: 'string',
      label: 'Twilio Phone Number',
      description: 'Your Twilio phone number (E.164 format)',
      defaultValue: '',
      placeholder: '+1234567890',
      visible: (settings) => settings['services-notifications.smsEnabled'],
      scope: ['tenant'],
      validation: {
        pattern: '^\\+[1-9]\\d{1,14}$',
        message: 'Must be in E.164 format (+1234567890)'
      }
    },
    
    // === PUSH NOTIFICATION CONFIGURATION ===
    
    pushEnabled: {
      type: 'boolean',
      label: 'Enable Push Notifications',
      description: 'Enable push notifications (requires Firebase)',
      defaultValue: false,
      scope: ['tenant']
    },
    
    firebaseServerKey: {
      type: 'password',
      label: 'Firebase Server Key',
      description: 'Firebase Cloud Messaging server key',
      defaultValue: '',
      placeholder: 'Enter server key',
      visible: (settings) => settings['services-notifications.pushEnabled'],
      scope: ['tenant'],
      sensitive: true
    },
    
    // === WEBHOOK CONFIGURATION ===
    
    webhookEnabled: {
      type: 'boolean',
      label: 'Enable Webhook Notifications',
      description: 'Enable webhook delivery',
      defaultValue: false,
      scope: ['tenant', 'user']
    },
    
    webhookUrl: {
      type: 'string',
      label: 'Webhook URL',
      description: 'URL to POST notifications to',
      defaultValue: '',
      placeholder: 'https://your-webhook.com/notifications',
      visible: (settings) => settings['services-notifications.webhookEnabled'],
      scope: ['tenant', 'user'],
      validation: {
        pattern: '^https?://.+',
        message: 'Must be a valid URL'
      }
    },
    
    webhookSecret: {
      type: 'password',
      label: 'Webhook Secret',
      description: 'Secret for webhook signature verification',
      defaultValue: '',
      placeholder: 'Enter webhook secret',
      visible: (settings) => settings['services-notifications.webhookEnabled'],
      scope: ['tenant', 'user'],
      sensitive: true
    }
  }
};

export default notificationsModule;
